'use client';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

export type HolidayTheme =
  | 'none'
  | 'christmas'
  | 'halloween'
  | 'thanksgiving'
  | 'independence'
  | 'valentines'
  | 'easter'
  | 'newYear';

export interface BrandSettings {
  appName: string;
  appSubtitle: string;
  logoUrl: string;
  primaryColor: string;
  accentColor: string;
  sidebarBg: string;
  holidayTheme: HolidayTheme;
}

export const DEFAULT_BRAND: BrandSettings = {
  appName: 'LegacyBridge',
  appSubtitle: 'Financial Group',
  logoUrl: '/assets/images/LBFG_2026_LOGO_2-1775061184218.png',
  primaryColor: '#0A1F44',
  accentColor: '#C9A84C',
  sidebarBg: '#0A1F44',
  holidayTheme: 'none',
};

export const HOLIDAY_THEMES: Record<
  HolidayTheme,
  { label: string; emoji: string; banner: string; colors?: Partial<BrandSettings> }
> = {
  none: { label: 'No Holiday Theme', emoji: '🏢', banner: '' },
  christmas: {
    label: 'Christmas',
    emoji: '🎄',
    banner: 'bg-gradient-to-r from-red-700 via-green-700 to-red-700',
    colors: { accentColor: '#c0392b' },
  },
  halloween: {
    label: 'Halloween',
    emoji: '🎃',
    banner: 'bg-gradient-to-r from-orange-600 via-gray-900 to-orange-600',
    colors: { accentColor: '#e67e22' },
  },
  thanksgiving: {
    label: 'Thanksgiving',
    emoji: '🦃',
    banner: 'bg-gradient-to-r from-amber-700 via-orange-600 to-amber-700',
    colors: { accentColor: '#d35400' },
  },
  independence: {
    label: '4th of July',
    emoji: '🇺🇸',
    banner: 'bg-gradient-to-r from-red-600 via-blue-700 to-red-600',
    colors: { accentColor: '#c0392b' },
  },
  valentines: {
    label: "Valentine's Day",
    emoji: '❤️',
    banner: 'bg-gradient-to-r from-pink-600 via-red-500 to-pink-600',
    colors: { accentColor: '#e91e8c' },
  },
  easter: {
    label: 'Easter',
    emoji: '🐣',
    banner: 'bg-gradient-to-r from-purple-400 via-yellow-300 to-pink-400',
    colors: { accentColor: '#9b59b6' },
  },
  newYear: {
    label: "New Year's",
    emoji: '🎆',
    banner: 'bg-gradient-to-r from-yellow-400 via-purple-700 to-yellow-400',
    colors: { accentColor: '#f1c40f' },
  },
};

interface BrandContextType {
  brand: BrandSettings;
  updateBrand: (updates: Partial<BrandSettings>) => void;
  resetBrand: () => void;
}

const BrandContext = createContext<BrandContextType>({
  brand: DEFAULT_BRAND,
  updateBrand: () => {},
  resetBrand: () => {},
});

export const useBrand = () => useContext(BrandContext);

const STORAGE_KEY = 'app_brand_settings';

export function BrandProvider({ children }: { children: React.ReactNode }) {
  const [brand, setBrand] = useState<BrandSettings>(DEFAULT_BRAND);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Always restore LBFG logo if stored value is missing or is the old generic logo
        if (!parsed.logoUrl || parsed.logoUrl === '/assets/images/app_logo.png') {
          parsed.logoUrl = DEFAULT_BRAND.logoUrl;
        }
        // Always enforce a dark sidebar — reset white or very light sidebar colors
        if (parsed.sidebarBg) {
          const clean = parsed.sidebarBg.replace('#', '');
          if (clean.length === 6) {
            const r = parseInt(clean.substring(0, 2), 16) / 255;
            const g = parseInt(clean.substring(2, 4), 16) / 255;
            const b = parseInt(clean.substring(4, 6), 16) / 255;
            const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
            if (luminance > 0.45) {
              parsed.sidebarBg = DEFAULT_BRAND.sidebarBg;
            }
          }
        }
        setBrand({ ...DEFAULT_BRAND, ...parsed });
      }
    } catch {
      // ignore
    }
  }, []);

  const updateBrand = useCallback((updates: Partial<BrandSettings>) => {
    setBrand((prev) => {
      const next = { ...prev, ...updates };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const resetBrand = useCallback(() => {
    setBrand(DEFAULT_BRAND);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }, []);

  return (
    <BrandContext.Provider value={{ brand, updateBrand, resetBrand }}>
      {children}
    </BrandContext.Provider>
  );
}
